/**
 * The colours of a page: its paper, and its house accent.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * **To add a paper or a preset colour, add a row to the table below.**
 *
 * As with the measurements, each table is the single definition of its
 * setting: the type and the list of ids follow from it, so a new paper is one
 * line here plus its two names in the message catalogue.
 * ─────────────────────────────────────────────────────────────────────────
 *
 * One rule governs the whole module, and it is a security rule rather than a
 * taste one: **a colour reaches a stylesheet, so anything accepted here is
 * written into CSS.** The accent is free text, and a report can be shared, so
 * a permissive check would let one person rewrite another's page. See
 * `readAccent`.
 *
 * Pure. Colours and names, no rendering.
 */

/**
 * The paper, and the ink and rules that read well on it.
 *
 * Kept together because they are one decision: cream paper with the ink meant
 * for white looks washed out, and choosing them separately would let somebody
 * arrive at that by accident.
 */
export const PAPERS = {
  white: { background: '#ffffff', ink: '#16202c', rule: '#dfe4ea' },
  /**
   * Warm and very slightly grey, as a printed annual report is. Easier on the
   * eye over forty pages, and it makes black text look blacker.
   */
  cream: { background: '#fbf9f4', ink: '#1b1a17', rule: '#e2ddd2' },
} as const

export type Paper = keyof typeof PAPERS

export const PAPER_IDS = Object.keys(PAPERS) as readonly Paper[]

/**
 * Colours worth offering before somebody reaches for the picker.
 *
 * Deliberately muted. A report is a document of record, and a house colour at
 * full saturation that looks striking on screen prints as a slab. Every one of
 * these also holds up in black and white, which is how a great many of them
 * are still read.
 */
export const ACCENT_PRESETS = {
  ink: '#16202c',
  navy: '#1c3f6e',
  teal: '#0f6b68',
  forest: '#2c5b3f',
  burgundy: '#7a2e3c',
  bronze: '#8a5a2b',
} as const

export type AccentPreset = keyof typeof ACCENT_PRESETS

export const ACCENT_PRESET_IDS = Object.keys(ACCENT_PRESETS) as readonly AccentPreset[]

export const DEFAULT_ACCENT: string = ACCENT_PRESETS.ink

/**
 * Six hex digits, and nothing else.
 *
 * **Strict on purpose.** The value ends up in a CSS custom property, so a
 * permissive check would let `red; } * { display: none } /*` through, and a
 * report shared with a colleague would then be able to rewrite their page.
 * Named colours, `rgb()` and shorthand are all refused, not because they are
 * dangerous in themselves but because allowing any of them means parsing CSS,
 * and parsing CSS to decide what is safe is how this goes wrong.
 */
const HEX = /^#[0-9a-f]{6}$/i

/** Whether a colour may be written into a stylesheet. */
export function isAccent(value: string): boolean {
  return HEX.test(value)
}

/**
 * A colour, if it is safe, or the default.
 *
 * Falls back rather than repairing: something almost right is worse than
 * something known, because it looks deliberate.
 */
export function readAccent(value: unknown): string {
  return typeof value === 'string' && HEX.test(value) ? value.toLowerCase() : DEFAULT_ACCENT
}

/**
 * Ink that reads on a colour: white on a dark one, the paper's ink on a light
 * one. By relative luminance, the measure contrast is defined in.
 */
export function readableInk(colour: string): string {
  if (!isAccent(colour)) return '#ffffff'
  const [red = 0, green = 0, blue = 0] = channels(colour).map((value) =>
    value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4,
  )
  const luminance = 0.2126 * red + 0.7152 * green + 0.0722 * blue
  // Where white and dark ink contrast equally: white below it, dark above.
  return luminance > 0.179 ? '#16202c' : '#ffffff'
}

/** A colour's three channels, each from 0 to 1. */
function channels(colour: string): number[] {
  return [1, 3, 5].map((at) => Number.parseInt(colour.slice(at, at + 2), 16) / 255)
}

/**
 * The charter's colours beside the main one: as many as a house style
 * usually names, and few enough that each still means something.
 */
export const MAX_PALETTE = 5

/**
 * The charter's other colours, read off a stored document.
 *
 * Each kept only if it is six hex digits, for the same reason as the accent:
 * every one is written into a stylesheet. Repeats dropped, the rest bounded.
 */
export function readPalette(value: unknown): readonly string[] {
  if (!Array.isArray(value)) return []
  const kept: string[] = []
  for (const colour of value) {
    if (typeof colour !== 'string' || !isAccent(colour)) continue
    const lower = colour.toLowerCase()
    if (!kept.includes(lower)) kept.push(lower)
  }
  return kept.slice(0, MAX_PALETTE)
}

/**
 * Where a chart takes its colours from: shades of the accent, which keeps a
 * report in one colour, or the charter's colours in turn, for a house whose
 * charts are meant to be told apart at a glance.
 */
export const CHART_COLOURS = ['shades', 'palette'] as const

export type ChartColours = (typeof CHART_COLOURS)[number]

/** A colour as hue (degrees), saturation and lightness (0 to 1). */
function hsl(colour: string): [number, number, number] {
  const [red = 0, green = 0, blue = 0] = channels(colour)
  const high = Math.max(red, green, blue)
  const low = Math.min(red, green, blue)
  const lightness = (high + low) / 2
  if (high === low) return [0, 0, lightness]
  const spread = high - low
  const saturation = lightness > 0.5 ? spread / (2 - high - low) : spread / (high + low)
  const hue =
    high === red
      ? ((green - blue) / spread + (green < blue ? 6 : 0)) * 60
      : high === green
        ? ((blue - red) / spread + 2) * 60
        : ((red - green) / spread + 4) * 60
  return [hue, saturation, lightness]
}

/** Hue, saturation and lightness back to `#rrggbb`. */
function fromHsl(hue: number, saturation: number, lightness: number): string {
  const turn = (((hue % 360) + 360) % 360) / 360
  const above =
    lightness < 0.5 ? lightness * (1 + saturation) : lightness + saturation - lightness * saturation
  const below = 2 * lightness - above
  const channel = (offset: number): string => {
    let at = turn + offset
    if (at < 0) at += 1
    if (at > 1) at -= 1
    const value =
      at < 1 / 6
        ? below + (above - below) * 6 * at
        : at < 1 / 2
          ? above
          : at < 2 / 3
            ? below + (above - below) * (2 / 3 - at) * 6
            : below
    return Math.round(value * 255)
      .toString(16)
      .padStart(2, '0')
  }
  return `#${channel(1 / 3)}${channel(0)}${channel(-1 / 3)}`
}

/**
 * Colours that sit with an accent, for somebody who has a house colour and
 * not yet the rest of a charter.
 *
 * Turned round the colour wheel from the accent, at about its depth and no
 * more saturated than a report wants: printed, a full-strength colour is a
 * slab. A grey accent (black, say) has no hue to turn, so a quiet blue stands
 * in for one. Suggestions only: somebody still chooses.
 */
export function suggestPalette(accent: string): readonly string[] {
  const [hue, saturation, lightness] = hsl(readAccent(accent))
  const base = saturation < 0.08 ? 212 : hue
  const depth = Math.min(0.42, Math.max(0.3, lightness))
  const strength = Math.min(0.55, Math.max(0.32, saturation))
  return [
    // The accent lighter: the commonest second colour of a charter.
    fromHsl(base, Math.min(0.65, strength + 0.1), depth + 0.22),
    // Across the wheel, warm against a cool accent or the other way round.
    fromHsl(base + 180, strength * 0.85, depth + 0.04),
    // A neighbour, a little lighter.
    fromHsl(base - 40, strength * 0.9, depth + 0.1),
    // The accent nearly grey, for what should recede.
    fromHsl(base, strength * 0.35, depth + 0.28),
  ]
}
